/**
 * Expectation Form component
 * Using React Hook Form with Zod validation
 */

import { useEffect, useMemo, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import * as z from 'zod'
import { useCreateExpectation, useUpdateExpectation } from '@/services/api/expectations'
import { formatForDateInput, localToUtc, getTodayDateInput } from '@/utils/dateUtils'
import type { CreatePriceExpectationInput, UpdatePriceExpectationInput, PriceExpectation } from '@/types/expectation'
import { CurrencyInput } from '@/components/ui/currency-input'
import { DatePicker } from '@/components/common/DatePicker'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { toast } from 'sonner'
import { useGoldPrices } from '@/services/api/goldPrice'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { IconSparkles, IconRefresh } from '@tabler/icons-react'

const expectationFormSchema = z.object({
  name: z.string().min(1, 'Tên kỳ vọng là bắt buộc'),
  expectedPrice: z.number().min(1, 'Giá kỳ vọng phải lớn hơn 0'),
  targetDate: z.string().min(1, 'Ngày mục tiêu là bắt buộc'),
})

type ExpectationFormValues = z.infer<typeof expectationFormSchema>

interface ExpectationFormProps {
  expectation?: PriceExpectation
  onSuccess?: () => void
  onCancel?: () => void
}

export function ExpectationForm({ expectation, onSuccess, onCancel }: ExpectationFormProps) {
  const isEdit = !!expectation
  const createMutation = useCreateExpectation()
  const updateMutation = useUpdateExpectation()
  const { data: goldTypes = [] } = useGoldPrices('Hồ Chí Minh')
  const [typePriceMap, setTypePriceMap] = useState<Record<number, string>>({})

  const form = useForm<ExpectationFormValues>({
    resolver: zodResolver(expectationFormSchema),
    defaultValues: {
      name: expectation?.name || '',
      expectedPrice: expectation?.expectedPrice || 0,
      targetDate: expectation ? formatForDateInput(expectation.targetDate) : getTodayDateInput(),
    },
  })

  // Sync form when expectation changes
  useEffect(() => {
    if (expectation) {
      form.reset({
        name: expectation.name || '',
        expectedPrice: expectation.expectedPrice || 0,
        targetDate: formatForDateInput(expectation.targetDate),
      })
      if (expectation.assetPrices) {
        const map: Record<number, string> = {}
        expectation.assetPrices.forEach((ap) => {
          map[ap.goldTypeId] = ap.expectedPrice.toString()
        })
        setTypePriceMap(map)
      }
    }
  }, [expectation, form])

  // Init gold-type prices on create
  useEffect(() => {
    if (!isEdit && goldTypes.length) {
      const map: Record<number, string> = {}
      goldTypes.forEach((type) => {
        map[type.Id] = (form.getValues('expectedPrice') || 0).toString()
      })
      setTypePriceMap(map)
    }
  }, [goldTypes, isEdit, form])

  const handleQuickPercent = (percent: number) => {
    setTypePriceMap((prev) => {
      const next: Record<number, string> = {}
      goldTypes.forEach((type) => {
        const current = parseFloat(prev[type.Id] || '0')
        const updated = current + (current * percent) / 100
        next[type.Id] = updated > 0 ? updated.toString() : '0'
      })
      return next
    })
  }

  const handleQuickFixed = (value: number) => {
    setTypePriceMap((_prev) => {
      const next: Record<number, string> = {}
      goldTypes.forEach((type) => {
        next[type.Id] = value.toString()
      })
      return next
    })
  }

  const assetPricesInput = useMemo(() => {
    return goldTypes
      .map((type) => {
        const val = typePriceMap[type.Id]
        if (!val) return null
        const num = parseFloat(val)
        if (!num || num <= 0 || isNaN(num)) return null
        return { goldTypeId: type.Id, expectedPrice: num }
      })
      .filter(Boolean) as { goldTypeId: number; expectedPrice: number }[]
  }, [goldTypes, typePriceMap])

  const onSubmit = async (data: ExpectationFormValues) => {
    try {
      const targetDateUtc = localToUtc(new Date(data.targetDate))

      if (!assetPricesInput.length) {
        toast.error('Vui lòng nhập giá kỳ vọng cho ít nhất một loại vàng')
        return
      }

      if (isEdit && expectation) {
        const updateInput: UpdatePriceExpectationInput = {
          name: data.name,
          expectedPrice: data.expectedPrice,
          targetDate: targetDateUtc,
          assetPrices: assetPricesInput,
        }
        await updateMutation.mutateAsync({ id: expectation.id, input: updateInput })
      } else {
        const createInput: CreatePriceExpectationInput = {
          name: data.name,
          expectedPrice: data.expectedPrice,
          targetDate: targetDateUtc,
          assetPrices: assetPricesInput,
        }
        await createMutation.mutateAsync(createInput)
      }

      onSuccess?.()
      toast.success(isEdit ? 'Đã cập nhật kỳ vọng thành công' : 'Đã tạo kỳ vọng thành công')
    } catch (error) {
      console.error('Failed to save expectation:', error)
      const errorMessage = error instanceof Error ? error.message : 'Không thể lưu kỳ vọng. Vui lòng thử lại.'
      toast.error(errorMessage)
      form.setError('root', { message: errorMessage })
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      {form.formState.errors.root && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
          </CardContent>
        </Card>
      )}

      <FieldGroup>
        <Controller
          name="name"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="expectation-form-name">
                Tên kỳ vọng <span className="text-destructive">*</span>
              </FieldLabel>
              <Input
                id="expectation-form-name"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="VD: Kịch bản giá tăng Q4"
              />
              <FieldDescription>Đặt tên để dễ phân biệt giữa các kịch bản.</FieldDescription>
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="expectedPrice"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="expectation-form-expectedPrice">
                Giá kỳ vọng (VND) <span className="text-destructive">*</span>
              </FieldLabel>
              <CurrencyInput
                id="expectation-form-expectedPrice"
                name={field.name}
                value={field.value?.toString() || ''}
                onValueChange={(value) => {
                  const numValue = value ? parseFloat(value) : undefined
                  field.onChange(numValue)
                }}
                onBlur={field.onBlur}
                aria-invalid={fieldState.invalid}
                placeholder="Ví dụ: 75,000,000"
              />
              <FieldDescription>
                Nhập giá vàng kỳ vọng (đơn vị: VND). Ví dụ: 75,000,000
              </FieldDescription>
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="targetDate"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="expectation-form-targetDate">
                Ngày mục tiêu <span className="text-destructive">*</span>
              </FieldLabel>
              <DatePicker
                value={field.value}
                onChange={field.onChange}
                id="expectation-form-targetDate"
                aria-invalid={fieldState.invalid}
                placeholder="Chọn ngày mục tiêu"
              />
              <FieldDescription>
                Ngày bạn mong muốn đạt được giá kỳ vọng
              </FieldDescription>
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
      </FieldGroup>

      {/* Quick actions for all assets */}
      <Card>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <IconSparkles className="h-4 w-4 text-primary" stroke={1.5} />
            <p className="text-sm font-medium">Thiết lập nhanh giá kỳ vọng cho tất cả loại vàng</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[ -10, -5, 0, 5, 10, 20 ].map((pct) => (
              <Button
                key={pct}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuickPercent(pct)}
              >
                {pct > 0 ? `+${pct}%` : `${pct}%`}
              </Button>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleQuickFixed(form.getValues('expectedPrice') || 0)}
            >
              Dùng giá kỳ vọng chung
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Per-gold-type inputs in scrollable table */}
      <Card>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="font-medium">Giá kỳ vọng theo từng loại vàng</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setTypePriceMap((prev) => {
                  const next: Record<number, string> = {}
                  goldTypes.forEach((type) => {
                    next[type.Id] = prev[type.Id] || (form.getValues('expectedPrice') || 0).toString()
                  })
                  return next
                })
              }}
            >
              <IconRefresh className="h-4 w-4 mr-1" /> Điền theo giá chung
            </Button>
          </div>
          {goldTypes.length ? (
            <div className="max-h-80 overflow-y-auto border rounded-md">
              <div className="grid grid-cols-3 gap-3 px-3 py-2 text-sm font-semibold">
                <span>Loại vàng</span>
                <span>Giá hiện tại</span>
                <span>Giá kỳ vọng</span>
              </div>
              <div className="divide-y">
                {goldTypes.map((type) => (
                  <div key={type.Id} className="grid grid-cols-3 gap-3 px-3 py-2 items-center text-sm">
                    <div className="font-medium">{type.TypeName}</div>
                    <div className="text-muted-foreground">{type.Sell}</div>
                    <CurrencyInput
                      value={typePriceMap[type.Id] || ''}
                      onValueChange={(value) =>
                        setTypePriceMap((prev) => ({ ...prev, [type.Id]: value || '' }))
                      }
                      placeholder="VD: 75,000,000"
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Không tải được danh sách loại vàng. Vui lòng thử lại.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="space-y-3">
        {(createMutation.isPending || updateMutation.isPending) && (
          <Progress value={100} className="w-full" />
        )}
        <div className="flex gap-2 justify-end">
          {onCancel && (
            <Button type="button" onClick={onCancel} variant="outline" className="min-h-[44px]" disabled={createMutation.isPending || updateMutation.isPending}>
              Hủy
            </Button>
          )}
          <Button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
            className="min-h-[44px]"
          >
            {createMutation.isPending || updateMutation.isPending
              ? 'Đang lưu...'
              : isEdit
                ? 'Cập nhật'
                : 'Tạo mới'}
          </Button>
        </div>
      </div>
    </form>
  )
}
